import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import AxleInvoicesPanel from './AxleInvoicesPanel';
import axlePushService, {
  AxlePushRow,
  AxlePushSummary,
  PayloadPreview,
} from '../../services/axlePush.service';

/** FastAPI can return `detail` as an array/object (validation errors) — only
 * render it when it's actually a string, else fall back. */
const errDetail = (err: any, fallback: string) => {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const formatTimestamp = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const statusChip = (row: AxlePushRow) => {
  if (row.last_error) {
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
        Failing
      </span>
    );
  }
  if (row.delivery_id) {
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
        Delivered
      </span>
    );
  }
  return (
    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
      Pending
    </span>
  );
};

const AxlePush: React.FC = () => {
  const [rows, setRows] = useState<AxlePushRow[]>([]);
  const [summary, setSummary] = useState<AxlePushSummary>({ total: 0, delivered: 0, failing: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);

  // Payload preview modal
  const [previewRecno, setPreviewRecno] = useState<number | null>(null);
  const [preview, setPreview] = useState<PayloadPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Per-row resend state + last result notice
  const [resendingRecno, setResendingRecno] = useState<number | null>(null);
  const [notice, setNotice] = useState<{ kind: 'success' | 'info' | 'error'; text: string } | null>(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await axlePushService.list();
      setRows(data.rows);
      setSummary(data.summary);
      setLastLoadedAt(new Date());
    } catch (err: any) {
      setError(errDetail(err, err?.message || 'Failed to load the Axle push ledger'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const openPreview = useCallback(async (recno: number) => {
    setPreviewRecno(recno);
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      setPreview(await axlePushService.payload(recno));
    } catch (err: any) {
      setPreviewError(errDetail(err, err?.message || 'Failed to build the payload'));
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const closePreview = useCallback(() => {
    setPreviewRecno(null);
    setPreview(null);
    setPreviewError(null);
  }, []);

  const doResend = useCallback(async (recno: number) => {
    if (!window.confirm(`Re-push repair order ${recno} to Axle now? This bypasses the no-change check.`)) {
      return;
    }
    setResendingRecno(recno);
    setNotice(null);
    try {
      const result = await axlePushService.resend(recno);
      if (result.status === 'sent') {
        setNotice({ kind: 'success', text: `Repair order ${recno} was sent to Axle.` });
      } else {
        setNotice({
          kind: 'info',
          text: `Repair order ${recno} was not sent — it doesn't qualify (not Completed and Axle never had it) or can't be built.`,
        });
      }
    } catch (err: any) {
      setNotice({
        kind: 'error',
        text: errDetail(err, err?.message || `Re-push of repair order ${recno} failed`),
      });
    } finally {
      setResendingRecno(null);
    }
    loadRows();
  }, [loadRows]);

  const columns = useMemo<ColumnDef<AxlePushRow>[]>(() => [
    {
      id: 'repairorder_recno',
      accessorKey: 'repairorder_recno',
      header: 'RO #',
      size: 90,
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => (row.last_error ? 'Failing' : row.delivery_id ? 'Delivered' : 'Pending'),
      cell: ({ row }) => statusChip(row.original),
      size: 110,
    },
    {
      id: 'pronumber',
      accessorKey: 'pronumber',
      header: 'PRO',
      cell: ({ row }) => row.original.pronumber ?? '',
      size: 100,
    },
    {
      id: 'chassisid',
      accessorKey: 'chassisid',
      header: 'Chassis',
      cell: ({ row }) => row.original.chassisid || '',
      size: 120,
    },
    {
      id: 'container',
      accessorKey: 'container',
      header: 'Container',
      cell: ({ row }) => row.original.container || '',
      size: 130,
    },
    {
      id: 'orderstatus',
      accessorKey: 'orderstatus',
      header: 'Order Status',
      cell: ({ row }) => row.original.orderstatus || '',
      size: 120,
    },
    {
      id: 'delivery_id',
      accessorKey: 'delivery_id',
      header: 'Delivery ID',
      cell: ({ row }) => (
        <span className="block max-w-[160px] truncate" title={row.original.delivery_id || ''}>
          {row.original.delivery_id || ''}
        </span>
      ),
      size: 170,
    },
    {
      id: 'last_pushed_at',
      accessorKey: 'last_pushed_at',
      header: 'Last Pushed',
      cell: ({ row }) => formatTimestamp(row.original.last_pushed_at),
      size: 170,
    },
    {
      id: 'attempts',
      accessorKey: 'attempts',
      header: 'Attempts',
      cell: ({ row }) => row.original.attempts ?? 0,
      size: 90,
    },
    {
      id: 'last_error',
      accessorKey: 'last_error',
      header: 'Last Error',
      cell: ({ row }) => (
        <span
          className="block max-w-[280px] truncate text-red-600 dark:text-red-400"
          title={row.original.last_error || ''}
        >
          {row.original.last_error || ''}
        </span>
      ),
      size: 300,
    },
    {
      id: 'updated_at',
      accessorKey: 'updated_at',
      header: 'Updated',
      cell: ({ row }) => formatTimestamp(row.original.updated_at),
      size: 170,
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => {
        const recno = row.original.repairorder_recno;
        const busy = resendingRecno === recno;
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => openPreview(recno)}
              className="px-2 py-1 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Payload
            </button>
            <button
              onClick={() => doResend(recno)}
              disabled={resendingRecno !== null}
              className="px-2 py-1 text-xs font-medium rounded-md border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-50"
            >
              {busy ? 'Pushing…' : 'Re-push'}
            </button>
          </div>
        );
      },
      size: 160,
    },
  ], [openPreview, doResend, resendingRecno]);

  return (
    <div className="h-full w-full p-10 pb-24">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-heading">Axle Push</h1>
          <p className="text-sm text-muted">
            Repair orders pushed to Axle Mobility — delivery state, failures, payload preview and manual re-push (up to 2,000 rows).
            {lastLoadedAt && ` Loaded ${lastLoadedAt.toLocaleTimeString()}.`}
          </p>
        </div>
        <button
          onClick={loadRows}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {notice && (
        <div
          className={`mb-4 p-3 rounded-lg border text-sm flex items-start justify-between gap-3 ${
            notice.kind === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
              : notice.kind === 'error'
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
                : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300'
          }`}
        >
          <span>{notice.text}</span>
          <button onClick={() => setNotice(null)} className="shrink-0 opacity-70 hover:opacity-100">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      <AxleInvoicesPanel />

      <TanTable
        data={rows}
        columns={columns}
        loading={loading}
        settingsKey="axle-push-table"
        fieldName="axle-push-table"
        searchPlaceholder="Search by RO, PRO, chassis, container, error..."
        enableColumnFilters={true}
        cardTitle={`Axle Push (${summary.delivered} delivered, ${summary.failing} failing)`}
      />

      {/* Payload preview modal — portaled: the page shell's stacking context
          makes in-page overlays misbehave (PermissionConfigurable precedent). */}
      {previewRecno !== null && createPortal(
        <>
          <div className="fixed inset-0 z-[9998] bg-scrim/40" onClick={closePreview} />
          <div className="fixed z-[9999] inset-0 flex items-center justify-center p-6 pointer-events-none">
            <div className="pointer-events-auto w-full max-w-3xl max-h-[85vh] flex flex-col bg-panel rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <h2 className="text-sm font-semibold text-heading">
                    Axle payload — repair order {previewRecno}
                  </h2>
                  {preview?.differs_from_last_sent && (
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      Differs from last sent
                    </span>
                  )}
                </div>
                <button
                  onClick={closePreview}
                  className="p-1 rounded-md text-muted hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 overflow-auto">
                {previewLoading && (
                  <p className="text-sm text-muted">Building payload…</p>
                )}
                {previewError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{previewError}</p>
                )}
                {preview && (
                  <pre className="text-xs whitespace-pre-wrap break-all text-gray-800 dark:text-gray-200">
                    {JSON.stringify(preview.payload, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default AxlePush;
